import { Button, Collapse, Spin } from 'antd';
import { MenuInfo } from 'rc-menu/lib/interface';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SINK_OPTIONS, SortBy } from '@common/enums';
import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { Filters, SectionSubtitle, SortByMenu } from '@core/components';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import { useSelectCutoutLabor } from '@ee-api/cutout-labor-api/hooks/useCutoutLaborProducts';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { DrawingShapeContext } from '@ee-context';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import {
    SinkFilters,
    resetSinkSelectedOption,
    selectSinkSelectedOption,
    selectSinkSortBy,
    setSinkSortBy,
    updateSinkFilters,
    updateSinkSelectedOption,
} from '@redux/slices/sinkSlice';

import SupplyIcon from '../../../../../../public/assets/icons/supply.svg';
import TrashIcon from '../../../../../../public/assets/icons/trash.svg';
import ChipMinimizer from '../ChipMinimizerSection/ChipMinimizerSection';
import useAppliance from '../appliances/useAppliance';
import { SinkItem } from './SinkItem';
import { sortSinks } from './sinks.utils';
import { useAreSinksAvailable } from './useAreSinksAvailable';
import useFilteredSinks from './useFilteredSinks';
import useSink from './useSink';

import './sink.scss';

const { Panel } = Collapse;

const formattedHeader = (subtitle: string, styleAsLink = false) => {
    return <div className={`appliance-group-title ${styleAsLink ? 'asLink' : ''}`}>{subtitle}</div>;
};

const SinkOptions = ({ setIsModalVisible }: { setIsModalVisible: (val: boolean) => void }) => {
    const sinkSelectedOption = useAppSelector(selectSinkSelectedOption);
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const sortBy = useAppSelector(selectSinkSortBy);

    const { applianceTypesInDrawingArea } = useAppliance();

    const sinkSortOption = [SortBy.TrendingNow, SortBy.LowToHigh, SortBy.HighToLow, SortBy.NameAZ, SortBy.NameZA];

    const ref = useRef<HTMLDivElement | null>(null);

    // TODO: Fix these functions and deduplicate them to be a generic implementation
    const scrollToLoadedSinks = () => {
        const estimatePageElm = document.getElementById('EstimatePage');
        const drawingArea = document.getElementsByClassName('drawing-area')[0];

        if (!estimatePageElm || !drawingArea || !ref || !ref.current) {
            return;
        }

        estimatePageElm.scrollTo({
            top: estimatePageElm.scrollTop + ref.current.getBoundingClientRect().bottom - drawingArea.clientHeight,
        });
    };

    const selectSinkOption = (sinkOption: SINK_OPTIONS) => {
        if (sinkSelectedOption === sinkOption) {
            dispatch(resetSinkSelectedOption());
            return;
        }

        dispatch(updateSinkSelectedOption(sinkOption));
        if (sinkOption === SINK_OPTIONS.NO_SINKS) {
            if (applianceTypesInDrawingArea([ApplianceType.SINK, ApplianceType.SINK_LABOR])) {
                setIsModalVisible(true);
            }
        } else {
            scrollToLoadedSinks();
        }
    };

    const setSortingOption = (e: MenuInfo) => {
        dispatch(setSinkSortBy(e.key as unknown as SortBy));
    };

    return (
        <>
            <div ref={ref} className="appliance-options sink">
                {Object.keys(SINK_OPTIONS).map((sinkOption, index) => (
                    <Button
                        key={index}
                        className={`${sinkOption === sinkSelectedOption ? 'selected' : ''}`}
                        onClick={() => selectSinkOption(sinkOption as SINK_OPTIONS)}
                    >
                        {t(`SCROLLABLE_AREA.SINK.OPTIONS.${sinkOption}`)}
                    </Button>
                ))}
            </div>
            {(sinkSelectedOption === SINK_OPTIONS.BROWSE_SINKS || sinkSelectedOption === SINK_OPTIONS.SUPPLY_SINKS) && (
                <>
                    <div className="title title-with-sort">
                        {t(
                            `SCROLLABLE_AREA.SINK.${
                                sinkSelectedOption === SINK_OPTIONS.BROWSE_SINKS
                                    ? 'BROWSE_SINK_INFO'
                                    : 'SUPPLIED_SINK_INFO'
                            }`
                        )}

                        {sinkSelectedOption !== SINK_OPTIONS.SUPPLY_SINKS && (
                            <SortByMenu sortBy={sortBy} setSortingOption={setSortingOption} options={sinkSortOption} />
                        )}
                    </div>
                </>
            )}
        </>
    );
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const SinkContent = ({ setIsModalVisible }: { setIsModalVisible: (val: boolean) => void }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const { isFetchingSinks, customerSuppliedSinks } = useSink();
    const { filteredSinks, sinkFilters } = useFilteredSinks();

    const [fixedPanels, setFixedPanels] = useState<string[]>([ApplianceSource.PROGRAM]);
    const [isFilterExpanded, setIsFilterExpanded] = useState(false);

    const { selectSinkCutout } = useSelectCutoutLabor();
    const { selectedValue: selectedMaterial } = useSelectedMaterial();

    const sinkSelectedOption = useAppSelector(selectSinkSelectedOption);

    const sortBy = useAppSelector(selectSinkSortBy);

    const sinks = useMemo(
        () =>
            // eslint-disable-next-line no-nested-ternary
            sinkSelectedOption === SINK_OPTIONS.BROWSE_SINKS
                ? filteredSinks
                : sinkSelectedOption === SINK_OPTIONS.SUPPLY_SINKS
                ? customerSuppliedSinks
                : [],
        [customerSuppliedSinks, filteredSinks, sinkSelectedOption]
    );

    const cutoutsForSinks = useMemo(
        () =>
            new Map(
                sinks.map((sink) => [
                    sink.productId,
                    selectSinkCutout({
                        materialType: selectedMaterial?.productLine,
                        sink: sink,
                        _source: `cutoutsForSinks ${sink.productId}`,
                    }),
                ])
            ),
        [selectSinkCutout, selectedMaterial?.productLine, sinks]
    );

    // various show/hide options determined by availability of different offerings
    const hasProgramItems = sinks.some((sink: ApplianceModel) => {
        return sink.productSource === ApplianceSource.PROGRAM;
    });

    const hasStoreItems = sinks.some((sink: ApplianceModel) => {
        return sink.productSource === ApplianceSource.IN_STORE;
    });

    function callback(key: string | string[]) {
        if (typeof key !== 'string') {
            setFixedPanels(key);
        }
    }

    const filterSinksByApplianceSource = useCallback(
        (applianceSource: ApplianceSource) =>
            (sinks: ApplianceModel[]): JSX.Element[] => {
                return sortSinks(
                    sinks.filter((sink) => sink.productSource === applianceSource),
                    sortBy
                ).map((sink, id) => <SinkItem key={id} sink={sink} cutout={cutoutsForSinks.get(sink.productId)} />);
            },
        [cutoutsForSinks, sortBy]
    );

    const programSinks = useMemo(
        () => filterSinksByApplianceSource(ApplianceSource.PROGRAM)(sinks),
        [filterSinksByApplianceSource, sinks]
    );

    const inStoreSinks = useMemo(
        () => filterSinksByApplianceSource(ApplianceSource.IN_STORE)(sinks),
        [filterSinksByApplianceSource, sinks]
    );

    return (
        <>
            <SinkOptions setIsModalVisible={setIsModalVisible} />
            {isFetchingSinks ? (
                <Spin style={{ fontSize: 24 }} />
            ) : (
                <div className={`sink-card-wrapper ${isFilterExpanded ? 'row-direction' : ''}`}>
                    {sinkSelectedOption === SINK_OPTIONS.BROWSE_SINKS ? (
                        <Filters
                            isFilterExpanded={isFilterExpanded}
                            setFilterExpanded={setIsFilterExpanded}
                            filterOptions={{ ...sinkFilters }}
                            updateFilters={(sinkFilters: SinkFilters) => dispatch(updateSinkFilters(sinkFilters))}
                        />
                    ) : null}
                    {sinks.length ? (
                        <div className="sink-group-wrapper">
                            {sinkSelectedOption === SINK_OPTIONS.BROWSE_SINKS ? (
                                <Collapse
                                    bordered={false}
                                    ghost
                                    defaultActiveKey={fixedPanels}
                                    destroyInactivePanel={false}
                                    onChange={(e: string | string[]) => callback(e)}
                                >
                                    {hasProgramItems ? (
                                        <Panel
                                            key={ApplianceSource.PROGRAM}
                                            header={formattedHeader(
                                                t('APPLIANCE_GROUP.' + ApplianceSource.PROGRAM.toUpperCase()),
                                                false
                                            )}
                                            showArrow={false}
                                            collapsible={'disabled'} // program appliances are always visible
                                        >
                                            <div className="card-appliance">{programSinks}</div>
                                        </Panel>
                                    ) : null}
                                    {hasStoreItems ? (
                                        <Panel
                                            key={ApplianceSource.IN_STORE}
                                            header={formattedHeader(
                                                t('APPLIANCE_GROUP.' + ApplianceSource.IN_STORE.toUpperCase()),
                                                true
                                            )}
                                            showArrow={false}
                                            collapsible={hasStoreItems ? 'header' : 'disabled'}
                                        >
                                            <div className="card-appliance">{inStoreSinks}</div>
                                        </Panel>
                                    ) : null}
                                </Collapse>
                            ) : (
                                <div className="card-appliance">
                                    {sortSinks(sinks, sortBy).map((sink, id) => (
                                        <SinkItem key={id} sink={sink} cutout={cutoutsForSinks.get(sink.productId)} />
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : null}
                </div>
            )}
        </>
    );
};

const Sink = (): JSX.Element => {
    const { areSinksAvailable } = useAreSinksAvailable();
    const { t } = useTranslation();
    const [isModalVisible, setIsModalVisible] = useState(false);

    const dispatch = useAppDispatch();

    const { deleteAllAppliancesByType } = useAppliance();

    const { updateActiveDrawingShape, lastActiveDrawingShape } = useContext(DrawingShapeContext);

    const sinkSelectedOption = useAppSelector(selectSinkSelectedOption);

    const onConfirmFromConfirmationModal = () => {
        deleteAllAppliancesByType([ApplianceType.SINK, ApplianceType.SINK_LABOR]);
        setIsModalVisible(false);
        updateActiveDrawingShape(lastActiveDrawingShape);
    };

    const onCancelFromConfirmationModal = () => {
        dispatch(resetSinkSelectedOption());
        setIsModalVisible(false);
    };

    return (
        <>
            <ConfirmationDialog
                visible={isModalVisible}
                onCancel={onCancelFromConfirmationModal}
                onConfirm={onConfirmFromConfirmationModal}
                icon={sinkSelectedOption === SINK_OPTIONS.SUPPLY_SINKS ? <SupplyIcon /> : <TrashIcon />}
                title={
                    sinkSelectedOption === SINK_OPTIONS.SUPPLY_SINKS
                        ? t('SCROLLABLE_AREA.SINK.SUPPLY_SINK_TITLE')
                        : t('SCROLLABLE_AREA.SINK.NO_SINK_IN_SPACE_TITLE')
                }
                content={
                    sinkSelectedOption === SINK_OPTIONS.SUPPLY_SINKS
                        ? t('SCROLLABLE_AREA.SINK.SUPPLY_SINK_DESCRIPTION')
                        : t('SCROLLABLE_AREA.SINK.NO_SINK_IN_SPACE_DESCRIPTION')
                }
            />
            {areSinksAvailable && (
                <div>
                    <SectionSubtitle
                        name={t('SCROLLABLE_AREA.SINK.TITLE')}
                        description={t('SCROLLABLE_AREA.SINK.DESCRIPTION')}
                        showLine
                    />

                    <SinkContent setIsModalVisible={setIsModalVisible} />
                    <ChipMinimizer />
                </div>
            )}
        </>
    );
};

export default Sink;
